import React, { useState, useEffect } from 'react';
import {
  PencilIcon,
  ArrowUturnLeftIcon,
  CalculatorIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusCircleIcon,
  ShieldExclamationIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';

// ─── Provenance ─────────────────────────────────────────────────────────────

type Provenance = 'saisi' | 'moyenne' | 'calcule';

const PROVENANCE_META: Record<
  Provenance,
  { label: string; colorClass: string; icon: React.ComponentType<{ className?: string }> }
> = {
  saisi:    { label: 'Saisi',    colorClass: 'bg-violet-100 text-violet-700 border-violet-200', icon: PencilIcon },
  moyenne:  { label: 'Moyenne',  colorClass: 'bg-gray-100 text-gray-600 border-gray-200',       icon: ChartBarIcon },
  calcule:  { label: 'Calculé',  colorClass: 'bg-green-100 text-green-700 border-green-200',    icon: CalculatorIcon },
};

const ProvenanceBadge: React.FC<{ type: Provenance; detail?: string }> = ({ type, detail }) => {
  const { label, colorClass, icon: Icon } = PROVENANCE_META[type];
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0 text-[10px] rounded-full border ${colorClass}`}>
      <Icon className="w-2.5 h-2.5" />
      {label}{detail ? ` · ${detail}` : ''}
    </span>
  );
};

// ─── Editable value (click-to-edit, compact) ────────────────────────────────

interface EditableValueProps {
  value: number;
  unit: string;
  provenance: Provenance;
  provenanceDetail?: string;
  onChange: (v: number) => void;
  onReset?: () => void;
  size?: 'md' | 'lg';
  muted?: boolean;
  hideBadge?: boolean;
}

const EditableValue: React.FC<EditableValueProps> = ({
  value, unit, provenance, provenanceDetail, onChange, onReset,
  size = 'md', muted = false, hideBadge = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);

  const commit = () => {
    const parsed = parseFloat(draft);
    if (!isNaN(parsed) && parsed !== value) onChange(parsed);
    setEditing(false);
  };

  const textSize = size === 'lg' ? 'text-xl' : 'text-base';
  const inputWidth = size === 'lg' ? 'w-24' : 'w-20';
  const isOverridden = provenance === 'saisi';

  if (editing) {
    return (
      <div className="inline-flex items-center gap-1">
        <input
          autoFocus
          type="number"
          step="any"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') { setDraft(String(value)); setEditing(false); }
          }}
          className={`${inputWidth} px-1.5 py-0 border border-violet-400 rounded ${textSize} font-semibold text-violet-700 text-right focus:outline-none focus:ring-2 focus:ring-violet-500`}
        />
        <span className="text-xs text-gray-400 w-14">{unit}</span>
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center gap-1.5 ${muted ? 'opacity-50' : ''}`}>
      {!hideBadge && <ProvenanceBadge type={provenance} detail={provenanceDetail} />}
      {onReset && isOverridden && !muted && (
        <button onClick={onReset} className="text-gray-400 hover:text-gray-600" title="Réinitialiser">
          <ArrowUturnLeftIcon className="w-3 h-3" />
        </button>
      )}
      <button
        onClick={() => setEditing(true)}
        disabled={muted}
        className={`${textSize} font-semibold text-gray-900 ${muted ? '' : 'hover:text-violet-700'} decoration-dotted underline-offset-2`}
        title={muted ? '' : 'Cliquer pour modifier'}
      >
        {value.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
      </button>
      <span className="text-xs text-gray-400 w-14 text-left">{unit}</span>
    </div>
  );
};

// ─── Display-only value ─────────────────────────────────────────────────────

const DisplayValue: React.FC<{ value: number; unit: string; muted?: boolean; size?: 'md' | 'lg'; bold?: boolean; color?: string }> = ({
  value, unit, muted, size = 'md', bold = false, color,
}) => {
  const textSize = size === 'lg' ? 'text-xl' : 'text-base';
  return (
    <div className={`inline-flex items-center gap-1.5 ${muted ? 'opacity-50' : ''}`}>
      <span className={`${textSize} ${bold ? 'font-bold' : 'font-semibold'} ${color ?? 'text-gray-900'}`}>
        {value.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
      </span>
      <span className="text-xs text-gray-400 w-14 text-left">{unit}</span>
    </div>
  );
};

// ─── Source disclosure (inline, small) ──────────────────────────────────────

type ProjectSample = { nom: string; valeur: number };

type RefSource = {
  nbProjets: number;
  contextLabel: string;
  range: { min: number; max: number };
  projets: ProjectSample[];
};

const SourceDisclosure: React.FC<{ source: RefSource; unit: string; muted?: boolean }> = ({ source, unit, muted }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`pl-4 -mt-0.5 ${muted ? 'opacity-50' : ''}`}>
      <div className="flex items-center justify-between gap-2 py-0.5">
        <span className="text-[11px] text-gray-400 italic">
          médiane · {source.contextLabel} · intervalle {source.range.min.toLocaleString('fr-FR')}–{source.range.max.toLocaleString('fr-FR')} {unit}
        </span>
        <button
          onClick={() => setExpanded(!expanded)}
          className="inline-flex items-center gap-0.5 text-[11px] text-violet-600 hover:text-violet-800 whitespace-nowrap"
        >
          {expanded ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
          {expanded ? 'Masquer' : `Voir ${source.nbProjets} projets`}
        </button>
      </div>
      {expanded && (
        <div className="mt-1 bg-gray-50 border border-gray-200 rounded-md p-2 max-h-40 overflow-y-auto">
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
            {source.projets.map((p, i) => (
              <div key={i} className="flex items-center justify-between gap-2">
                <span className="text-gray-600 truncate">{p.nom}</span>
                <span className="font-mono text-gray-500 text-[10px] flex-shrink-0">
                  {p.valeur.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {unit}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Layout primitives ──────────────────────────────────────────────────────

const SectionHeader: React.FC<{ label: string; trailing?: string; muted?: boolean }> = ({ label, trailing, muted }) => (
  <div className={`flex items-center gap-3 pt-3 first:pt-0 ${muted ? 'opacity-50' : ''}`}>
    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-[0.08em] whitespace-nowrap">
      {label}
    </span>
    <div className="flex-1 h-px bg-gray-200" />
    {trailing && <span className="text-[10px] text-gray-400 italic whitespace-nowrap">{trailing}</span>}
  </div>
);

const Row: React.FC<{ label: string; children: React.ReactNode; muted?: boolean; bold?: boolean }> = ({
  label, children, muted, bold,
}) => (
  <div className={`flex items-center justify-between gap-4 py-1.5 ${muted ? 'opacity-50' : ''}`}>
    <span className={`text-sm ${bold ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>{label}</span>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

// ─── Mock data ──────────────────────────────────────────────────────────────

type TypologieKey = 'residentiel-neuf' | 'residentiel-rehab' | 'residentiel-haut-gamme';
type EstimatorUnit = 'logements' | 'm2-sdp';

const ESTIMATOR_UNITS: Record<EstimatorUnit, { label: string; shortLabel: string; defaultQty: number }> = {
  'logements': { label: 'Logements', shortLabel: 'logts',  defaultQty: 80   },
  'm2-sdp':    { label: 'm² SDP',    shortLabel: 'm² SDP', defaultQty: 5000 },
};

type CoeffDef = { value: number; unit: string; source: RefSource };
type PrixDef  = { value: number; unit: string; source: RefSource };

const TEMPLATES: Record<TypologieKey, {
  label: string;
  ratios: Record<EstimatorUnit, CoeffDef>;
  prix: PrixDef;
  mlHistoricalAvg: number;
}> = {
  'residentiel-neuf': {
    label: 'Résidentiel · Neuf',
    ratios: {
      'logements': {
        value: 60, unit: 'ml/logt',
        source: {
          nbProjets: 12, contextLabel: 'Résidentiel · Neuf · 2022-2025',
          range: { min: 45, max: 78 },
          projets: [
            { nom: 'Clichy · 42 logts',      valeur: 58 },
            { nom: 'Meudon · 68 logts',      valeur: 62 },
            { nom: 'Issy · 95 logts',        valeur: 56 },
            { nom: 'Gennevilliers · 110',    valeur: 52 },
            { nom: 'Saint-Denis · 75 logts', valeur: 64 },
            { nom: 'Nanterre · 48 logts',    valeur: 61 },
            { nom: 'Vitry · 88 logts',       valeur: 59 },
            { nom: 'Montreuil · 120 logts',  valeur: 48 },
            { nom: 'Bagneux · 36 logts',     valeur: 78 },
            { nom: 'Pantin · 92 logts',      valeur: 55 },
            { nom: 'Asnières · 60 logts',    valeur: 65 },
            { nom: 'Boulogne · 52 logts',    valeur: 72 },
          ],
        },
      },
      'm2-sdp': {
        value: 0.7, unit: 'ml/m² SDP',
        source: {
          nbProjets: 12, contextLabel: 'Résidentiel · Neuf · 2022-2025',
          range: { min: 0.55, max: 0.95 },
          projets: [
            { nom: 'Clichy · 2 940 m²',      valeur: 0.83 },
            { nom: 'Meudon · 4 760 m²',      valeur: 0.88 },
            { nom: 'Issy · 6 650 m²',        valeur: 0.80 },
            { nom: 'Gennevilliers · 7 700',  valeur: 0.74 },
            { nom: 'Saint-Denis · 5 250 m²', valeur: 0.91 },
            { nom: 'Nanterre · 3 360 m²',    valeur: 0.87 },
            { nom: 'Vitry · 6 160 m²',       valeur: 0.84 },
            { nom: 'Montreuil · 8 400 m²',   valeur: 0.69 },
            { nom: 'Bagneux · 2 520 m²',     valeur: 0.95 },
            { nom: 'Pantin · 6 440 m²',      valeur: 0.79 },
            { nom: 'Asnières · 4 200 m²',    valeur: 0.93 },
            { nom: 'Boulogne · 3 640 m²',    valeur: 0.55 },
          ],
        },
      },
    },
    prix: {
      value: 450, unit: '€/ml',
      source: {
        nbProjets: 12, contextLabel: 'Résidentiel · Neuf · 2022-2025',
        range: { min: 380, max: 540 },
        projets: [
          { nom: 'Clichy',        valeur: 435 },
          { nom: 'Meudon',        valeur: 462 },
          { nom: 'Issy',          valeur: 448 },
          { nom: 'Gennevilliers', valeur: 420 },
          { nom: 'Saint-Denis',   valeur: 455 },
          { nom: 'Nanterre',      valeur: 440 },
          { nom: 'Vitry',         valeur: 428 },
          { nom: 'Montreuil',     valeur: 380 },
          { nom: 'Bagneux',       valeur: 508 },
          { nom: 'Pantin',        valeur: 415 },
          { nom: 'Asnières',      valeur: 495 },
          { nom: 'Boulogne',      valeur: 540 },
        ],
      },
    },
    mlHistoricalAvg: 4200,
  },
  'residentiel-rehab': {
    label: 'Résidentiel · Réhab.',
    ratios: {
      'logements': {
        value: 45, unit: 'ml/logt',
        source: {
          nbProjets: 8, contextLabel: 'Résidentiel · Réhab. · 2022-2025',
          range: { min: 32, max: 62 },
          projets: [
            { nom: 'Paris 11e · 24 logts',   valeur: 42 },
            { nom: 'Paris 18e · 38 logts',   valeur: 48 },
            { nom: 'Levallois · 52 logts',   valeur: 40 },
            { nom: 'Neuilly · 18 logts',     valeur: 62 },
            { nom: 'Aubervilliers · 44',     valeur: 45 },
            { nom: 'Malakoff · 30 logts',    valeur: 50 },
            { nom: 'Saint-Ouen · 66 logts',  valeur: 38 },
            { nom: 'Charenton · 28 logts',   valeur: 32 },
          ],
        },
      },
      'm2-sdp': {
        value: 0.5, unit: 'ml/m² SDP',
        source: {
          nbProjets: 8, contextLabel: 'Résidentiel · Réhab. · 2022-2025',
          range: { min: 0.38, max: 0.72 },
          projets: [
            { nom: 'Paris 11e · 1 680 m²',   valeur: 0.60 },
            { nom: 'Paris 18e · 2 660 m²',   valeur: 0.69 },
            { nom: 'Levallois · 3 640 m²',   valeur: 0.57 },
            { nom: 'Neuilly · 1 260 m²',     valeur: 0.72 },
            { nom: 'Aubervilliers',          valeur: 0.52 },
            { nom: 'Malakoff · 2 100 m²',    valeur: 0.58 },
            { nom: 'Saint-Ouen',             valeur: 0.43 },
            { nom: 'Charenton · 1 960 m²',   valeur: 0.38 },
          ],
        },
      },
    },
    prix: {
      value: 620, unit: '€/ml',
      source: {
        nbProjets: 8, contextLabel: 'Résidentiel · Réhab. · 2022-2025',
        range: { min: 520, max: 780 },
        projets: [
          { nom: 'Paris 11e',     valeur: 585 },
          { nom: 'Paris 18e',     valeur: 640 },
          { nom: 'Levallois',     valeur: 610 },
          { nom: 'Neuilly',       valeur: 780 },
          { nom: 'Aubervilliers', valeur: 560 },
          { nom: 'Malakoff',      valeur: 620 },
          { nom: 'Saint-Ouen',    valeur: 520 },
          { nom: 'Charenton',     valeur: 635 },
        ],
      },
    },
    mlHistoricalAvg: 3100,
  },
  'residentiel-haut-gamme': {
    label: 'Résidentiel · Haut de gamme',
    ratios: {
      'logements': {
        value: 72, unit: 'ml/logt',
        source: {
          nbProjets: 6, contextLabel: 'Résidentiel · Haut de gamme · 2022-2025',
          range: { min: 58, max: 88 },
          projets: [
            { nom: 'Neuilly · 28 logts',       valeur: 85 },
            { nom: 'Paris 7e · 18 logts',      valeur: 88 },
            { nom: 'Versailles · 42 logts',    valeur: 68 },
            { nom: 'Saint-Cloud · 24 logts',   valeur: 74 },
            { nom: 'Sèvres · 36 logts',        valeur: 64 },
            { nom: 'La Celle-St-Cloud · 48',   valeur: 58 },
          ],
        },
      },
      'm2-sdp': {
        value: 0.85, unit: 'ml/m² SDP',
        source: {
          nbProjets: 6, contextLabel: 'Résidentiel · Haut de gamme · 2022-2025',
          range: { min: 0.70, max: 1.05 },
          projets: [
            { nom: 'Neuilly',              valeur: 1.02 },
            { nom: 'Paris 7e',             valeur: 1.05 },
            { nom: 'Versailles',           valeur: 0.82 },
            { nom: 'Saint-Cloud',          valeur: 0.89 },
            { nom: 'Sèvres',               valeur: 0.77 },
            { nom: 'La Celle-St-Cloud',    valeur: 0.70 },
          ],
        },
      },
    },
    prix: {
      value: 680, unit: '€/ml',
      source: {
        nbProjets: 6, contextLabel: 'Résidentiel · Haut de gamme · 2022-2025',
        range: { min: 580, max: 820 },
        projets: [
          { nom: 'Neuilly',           valeur: 780 },
          { nom: 'Paris 7e',          valeur: 820 },
          { nom: 'Versailles',        valeur: 640 },
          { nom: 'Saint-Cloud',       valeur: 695 },
          { nom: 'Sèvres',            valeur: 620 },
          { nom: 'La Celle-St-Cloud', valeur: 580 },
        ],
      },
    },
    mlHistoricalAvg: 4800,
  },
};

// ─── Provision ──────────────────────────────────────────────────────────────

type ProvisionType = 'pct' | 'abs';
type Provision = { type: ProvisionType; amount: number; motif: string };

// ─── Main component ─────────────────────────────────────────────────────────

const RatioFacadePrototype: React.FC = () => {
  const [typologie, setTypologie] = useState<TypologieKey>('residentiel-neuf');

  const [estimatorUnit, setEstimatorUnit] = useState<EstimatorUnit>('logements');
  const [estimatorQty, setEstimatorQty] = useState<number>(80);
  const [coeffOverride, setCoeffOverride] = useState<number | null>(null);

  const [exactMl, setExactMl] = useState<number | null>(null);

  const [prixOverride, setPrixOverride] = useState<number | null>(null);
  const [provision, setProvision] = useState<Provision | null>(null);

  const template = TEMPLATES[typologie];
  const coeffDef = template.ratios[estimatorUnit];
  const prixDef = template.prix;

  const estimatorCoeff = coeffOverride ?? coeffDef.value;
  const coeffProvenance: Provenance = coeffOverride !== null ? 'saisi' : 'moyenne';
  const estimatedMl = Math.round(estimatorQty * estimatorCoeff);

  const step1Muted = exactMl !== null;
  const activeMl = exactMl ?? estimatedMl;
  const activeMlProvenance: Provenance = exactMl !== null ? 'saisi' : 'calcule';

  const prixBase = prixOverride ?? prixDef.value;
  const prixProvenance: Provenance = prixOverride !== null ? 'saisi' : 'moyenne';
  const provisionDelta = provision
    ? (provision.type === 'pct' ? prixBase * provision.amount / 100 : provision.amount)
    : 0;
  const prixMl = prixBase + provisionDelta;

  const budget = activeMl * prixMl;

  const deviationPct = template.mlHistoricalAvg > 0
    ? ((activeMl - template.mlHistoricalAvg) / template.mlHistoricalAvg) * 100
    : 0;
  const showMlWarning =
    (activeMlProvenance === 'saisi' || coeffProvenance === 'saisi') &&
    Math.abs(deviationPct) > 15;

  const prixBaseDeviationPct = prixDef.value > 0
    ? ((prixBase - prixDef.value) / prixDef.value) * 100
    : 0;
  const showPrixWarning = prixProvenance === 'saisi' && Math.abs(prixBaseDeviationPct) > 20;

  const switchTypologie = (key: TypologieKey) => {
    setTypologie(key);
    setCoeffOverride(null);
    setExactMl(null);
    setPrixOverride(null);
    setProvision(null);
  };

  const switchEstimatorUnit = (key: EstimatorUnit) => {
    setEstimatorUnit(key);
    setEstimatorQty(ESTIMATOR_UNITS[key].defaultQty);
    setCoeffOverride(null);
  };

  return (
    <div className="space-y-3">
      {/* Intro — compact */}
      <p className="text-xs text-gray-500 italic px-1">
        Prototype lot <strong className="text-gray-700 not-italic">Façade</strong> · unité native <strong className="text-gray-700 not-italic">ml</strong>.
        Estimation rapide, métré exact prioritaire, provision pour atypicités — chaque valeur de référence affiche sa source.
      </p>

      {/* Main card */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <BuildingOffice2Icon className="w-5 h-5 text-gray-500" />
            <span className="font-semibold text-gray-900">Façade</span>
            <span className="text-gray-300">·</span>
            <span className="text-sm text-gray-600">{template.label}</span>
          </div>
          <div className="flex gap-1">
            {(Object.entries(TEMPLATES) as [TypologieKey, typeof TEMPLATES[TypologieKey]][]).map(([key, t]) => (
              <button
                key={key}
                onClick={() => switchTypologie(key)}
                className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors ${
                  typologie === key
                    ? 'bg-violet-600 text-white border-violet-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-violet-400 hover:text-violet-700'
                }`}
              >
                {t.label.split('·')[1]?.trim() ?? t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ESTIMATION */}
        <SectionHeader
          label="Estimation"
          trailing={step1Muted ? 'ignorée — métré exact saisi' : undefined}
          muted={step1Muted}
        />
        <Row label="Unité" muted={step1Muted}>
          <div className="flex gap-1">
            {(Object.entries(ESTIMATOR_UNITS) as [EstimatorUnit, typeof ESTIMATOR_UNITS[EstimatorUnit]][]).map(([key, u]) => (
              <button
                key={key}
                onClick={() => switchEstimatorUnit(key)}
                disabled={step1Muted}
                className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors ${
                  estimatorUnit === key
                    ? 'bg-violet-100 text-violet-700 border-violet-300'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300'
                } ${step1Muted ? 'cursor-not-allowed' : ''}`}
              >
                {u.label}
              </button>
            ))}
          </div>
        </Row>
        <Row label="Quantité" muted={step1Muted}>
          <EditableValue
            value={estimatorQty}
            unit={ESTIMATOR_UNITS[estimatorUnit].shortLabel}
            provenance="saisi"
            onChange={setEstimatorQty}
            muted={step1Muted}
            hideBadge
          />
        </Row>
        <Row label="Coefficient" muted={step1Muted}>
          <EditableValue
            value={estimatorCoeff}
            unit={coeffDef.unit}
            provenance={coeffProvenance}
            provenanceDetail={coeffProvenance === 'moyenne' ? `${coeffDef.source.nbProjets} projets` : undefined}
            onChange={(v) => setCoeffOverride(v)}
            onReset={() => setCoeffOverride(null)}
            muted={step1Muted}
          />
        </Row>
        {coeffProvenance === 'moyenne' && (
          <SourceDisclosure source={coeffDef.source} unit={coeffDef.unit} muted={step1Muted} />
        )}
        <Row label="Métré estimé" bold muted={step1Muted}>
          <DisplayValue value={estimatedMl} unit="ml" muted={step1Muted} bold />
        </Row>

        {/* MÉTRÉ EXACT */}
        <SectionHeader label="Métré exact" trailing="facultatif — prend la main" />
        {exactMl === null ? (
          <Row label="Métré façade">
            <input
              type="number"
              placeholder="saisir…"
              onBlur={(e) => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v) && v > 0) setExactMl(v);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const v = parseFloat((e.target as HTMLInputElement).value);
                  if (!isNaN(v) && v > 0) setExactMl(v);
                }
              }}
              className="w-24 px-1.5 py-0 border border-gray-300 rounded text-base font-semibold text-right focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            <span className="text-xs text-gray-400 w-14 text-left">ml</span>
          </Row>
        ) : (
          <Row label="Métré façade" bold>
            <button
              onClick={() => setExactMl(null)}
              className="inline-flex items-center gap-0.5 text-[11px] text-gray-400 hover:text-red-600"
              title="Effacer — réactive l'estimation"
            >
              <XMarkIcon className="w-3 h-3" /> effacer
            </button>
            <EditableValue
              value={exactMl}
              unit="ml"
              provenance="saisi"
              onChange={setExactMl}
            />
          </Row>
        )}

        {showMlWarning && (
          <div className="mt-1 flex items-start gap-1.5 text-[11px] text-orange-600">
            <ExclamationTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Métré retenu {activeMl.toLocaleString('fr-FR')} ml — écart de <strong>{deviationPct > 0 ? '+' : ''}{Math.round(deviationPct)}%</strong> vs moyenne historique ({template.mlHistoricalAvg.toLocaleString('fr-FR')} ml).
            </span>
          </div>
        )}

        {/* PRIX */}
        <SectionHeader label="Prix" />
        <Row label="Prix de base">
          <EditableValue
            value={prixBase}
            unit={prixDef.unit}
            provenance={prixProvenance}
            provenanceDetail={prixProvenance === 'moyenne' ? `${prixDef.source.nbProjets} projets` : undefined}
            onChange={(v) => setPrixOverride(v)}
            onReset={() => setPrixOverride(null)}
          />
        </Row>
        {prixProvenance === 'moyenne' && (
          <SourceDisclosure source={prixDef.source} unit={prixDef.unit} />
        )}
        {showPrixWarning && (
          <div className="flex items-start gap-1.5 text-[11px] text-orange-600 pl-4 -mt-0.5">
            <ExclamationTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Écart de <strong>{prixBaseDeviationPct > 0 ? '+' : ''}{Math.round(prixBaseDeviationPct)}%</strong> vs médiane historique.
            </span>
          </div>
        )}

        {/* Provision row */}
        <Row label="Provision">
          {provision === null ? (
            <button
              onClick={() => setProvision({ type: 'pct', amount: 10, motif: '' })}
              className="inline-flex items-center gap-1 text-[11px] text-violet-600 hover:text-violet-800 border border-dashed border-violet-300 rounded px-2 py-0.5 hover:border-violet-500 hover:bg-violet-50"
            >
              <PlusCircleIcon className="w-3 h-3" /> ajouter
            </button>
          ) : (
            <div className="inline-flex items-center gap-1.5">
              <ShieldExclamationIcon className="w-3.5 h-3.5 text-amber-600" />
              <div className="inline-flex rounded border border-amber-300 overflow-hidden text-[11px]">
                <button
                  onClick={() => setProvision({ ...provision, type: 'pct' })}
                  className={`px-1.5 py-0 ${provision.type === 'pct' ? 'bg-amber-500 text-white' : 'bg-white text-gray-600 hover:bg-amber-50'}`}
                >%</button>
                <button
                  onClick={() => setProvision({ ...provision, type: 'abs' })}
                  className={`px-1.5 py-0 border-l border-amber-300 ${provision.type === 'abs' ? 'bg-amber-500 text-white' : 'bg-white text-gray-600 hover:bg-amber-50'}`}
                >€/ml</button>
              </div>
              <input
                type="number"
                step="any"
                value={provision.amount}
                onChange={(e) => setProvision({ ...provision, amount: parseFloat(e.target.value) || 0 })}
                className="w-16 px-1.5 py-0 border border-amber-300 rounded text-base font-semibold text-amber-800 text-right focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <span className="text-xs text-gray-400 w-14 text-left">
                {provision.type === 'pct' ? '%' : '€/ml'}
              </span>
              <button
                onClick={() => setProvision(null)}
                className="text-gray-400 hover:text-red-600"
                title="Retirer la provision"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </Row>
        {provision !== null && (
          <>
            <Row label="Motif">
              <input
                type="text"
                value={provision.motif}
                onChange={(e) => setProvision({ ...provision, motif: e.target.value })}
                placeholder="ex: façade complexe, isolation renforcée…"
                className="w-64 px-1.5 py-0 border border-gray-200 rounded text-xs bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-amber-400"
              />
            </Row>
            <div className="pl-4 -mt-0.5 py-0.5">
              <span className="text-[11px] text-amber-700 italic">
                soit {provisionDelta >= 0 ? '+' : '−'}{Math.abs(provisionDelta).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {prixDef.unit}
              </span>
            </div>
            <Row label="Prix retenu" bold>
              <DisplayValue value={prixMl} unit={prixDef.unit} bold color="text-violet-700" />
            </Row>
          </>
        )}

        {/* BUDGET */}
        <div className="mt-4 pt-4 border-t-2 border-gray-100">
          <div className="flex items-end justify-between gap-4">
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-[0.08em]">Budget façade</span>
              <span className="text-xs text-gray-500">
                {activeMl.toLocaleString('fr-FR')} ml <span className="text-gray-400">({activeMlProvenance === 'saisi' ? 'exact' : 'estimé'})</span>
                {' × '}
                {prixMl.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {prixDef.unit}
              </span>
              {provision !== null && provisionDelta !== 0 && (
                <span className="text-[11px] text-amber-700 inline-flex items-center gap-1">
                  <ShieldExclamationIcon className="w-3 h-3" />
                  dont {provisionDelta >= 0 ? '+' : '−'}{Math.abs(activeMl * provisionDelta).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} € de provision{provision.motif ? ` · ${provision.motif}` : ''}
                </span>
              )}
            </div>
            <div className="text-3xl font-bold text-violet-700 whitespace-nowrap">
              {budget.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
            </div>
          </div>
        </div>
      </div>

      {/* Insights inline — très discrets */}
      <div className="px-1 text-[11px] text-gray-400 flex flex-wrap gap-x-3 gap-y-0.5">
        <span>▸ Source traçable sur chaque valeur</span>
        <span>▸ Estimation ou métré exact interchangeables</span>
        <span>▸ Provision motivée séparée du prix de base</span>
        <span>▸ Override + reset d'un clic</span>
      </div>
    </div>
  );
};

export default RatioFacadePrototype;
